import React, { useState, useEffect } from 'react';
import { seminarApi } from '../../lib/api';
import { formatCurrency } from '../../lib/utils';
import { Calendar, MapPin, Clock, Users, ExternalLink, Heart } from 'lucide-react';

export function SeminarWidget() {
    const [seminars, setSeminars] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadSeminars();
    }, []);

    const loadSeminars = async () => {
        try {
            const response = await seminarApi.getPublic();
            setSeminars(response.data);
        } catch (error) {
            console.error('Error loading seminars:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full"></div>
            </div>
        );
    }

    return (
        <div className="font-sans bg-white min-h-full">
            {/* Header */}
            <div className="bg-secondary text-white p-4">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary rounded-lg">
                        <Heart className="h-5 w-5" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold">BGPR Veranstaltungskalender</h2>
                        <p className="text-sm text-white/70">Fortbildungen für Herzgruppenleiter</p>
                    </div>
                </div>
            </div>

            {/* Seminars List */}
            <div className="p-4 space-y-4">
                {seminars.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                        <Calendar className="h-12 w-12 mx-auto mb-3 opacity-30" />
                        <p>Aktuell keine Veranstaltungen verfügbar</p>
                    </div>
                ) : (
                    seminars.map((seminar) => (
                        <div
                            key={seminar.id}
                            className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                            data-testid={`widget-seminar-${seminar.id}`}
                        >
                            <div className="flex justify-between items-start">
                                <div className="flex-1">
                                    <h3 className="font-semibold text-gray-900">{seminar.title}</h3>
                                    <p className="text-sm text-gray-500 mt-1">{seminar.organizer}</p>
                                </div>
                                <span className="bg-primary text-white text-xs font-bold px-2 py-1 rounded">
                                    {seminar.ue_count} UE
                                </span>
                            </div>

                            {seminar.description && (
                                <p className="text-sm text-gray-600 mt-2 line-clamp-2">
                                    {seminar.description}
                                </p>
                            )}

                            <div className="grid grid-cols-2 gap-3 mt-4 text-sm">
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Calendar className="h-4 w-4 text-primary" />
                                    <span>{seminar.date}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Clock className="h-4 w-4 text-primary" />
                                    <span>{seminar.time_start} - {seminar.time_end}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-600">
                                    <MapPin className="h-4 w-4 text-primary" />
                                    <span className="truncate">{seminar.location}</span>
                                </div>
                                <div className="flex items-center gap-2 text-gray-600">
                                    <Users className="h-4 w-4 text-primary" />
                                    <span>{seminar.current_participants}/{seminar.max_participants} Plätze</span>
                                </div>
                            </div>

                            <div className="flex items-center justify-between mt-4 pt-4 border-t">
                                <span className="text-lg font-bold text-primary">
                                    {formatCurrency(seminar.price)}
                                </span>
                                {seminar.external_link ? (
                                    <a
                                        href={seminar.external_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-md hover:bg-primary/80 transition-colors"
                                    >
                                        Zum Veranstalter
                                        <ExternalLink className="h-4 w-4" />
                                    </a>
                                ) : (
                                    <button
                                        className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                                            seminar.current_participants >= seminar.max_participants
                                                ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                                                : 'bg-primary text-white hover:bg-primary/80'
                                        }`}
                                        disabled={seminar.current_participants >= seminar.max_participants}
                                        data-testid={`widget-book-${seminar.id}`}
                                    >
                                        {seminar.current_participants >= seminar.max_participants
                                            ? 'Ausgebucht'
                                            : 'Anmelden'}
                                    </button>
                                )}
                            </div>
                        </div>
                    ))
                )}
            </div>

            {/* Footer */}
            <div className="border-t p-4 text-center text-sm text-gray-500">
                <a
                    href="https://www.bgpr.de"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                >
                    www.bgpr.de
                </a>
            </div>
        </div>
    );
}
